import { forwardRef, useEffect, useState } from 'react';
import { css } from '@emotion/react';

import { isNonEmpty } from '@toolbox/utils';
import { makeStyles } from '@toolbox/style';
import { StyledInput } from './StyledInput';
import { InputLabel } from './InputLabel';
import { InputContainer } from './InputContainer';
import { InputWrapper } from './InputWrapper';

import type { InputWrapperProps } from './InputWrapper';
import type { InputHTMLAttributes, FocusEvent, MouseEvent, ReactNode } from 'react';

export type InputProps = InputHTMLAttributes<HTMLInputElement> &
  InputWrapperProps & {
    suffix?: ReactNode;
    label?: ReactNode;
  };

const useStyles = makeStyles(({ theme }) => ({
  wrapper: css``,
  active: css`
    border-color: ${theme.palette.colors.primary};
  `,
  error: css`
    border-color: ${theme.palette.colors.error};
  `,
  disabled: css`
    opacity: 0.5;
  `,
  label: css`
    background-color: #ffffff;
  `,
  labelGreyBackground: css`
    background-color: #f7f8fa;
    @media (min-width: ${theme.breakpoints.md}) {
      background-color: #ffffff;
    }
  `,
  labelGreyBackgroundV3: css`
    background-color: #ffffff;
  `,
  activeLabel: css`
    top: 0;
    transform: translate(-${theme.spacing(1)}, -50%);
    color: ${theme.palette.colors.primary};
    padding: ${theme.spacing(1)};
    font-size: ${theme.fontSizes.f1};
    line-height: ${theme.lineHeights.xs};
  `,
  suffix: css`
    cursor: default;
  `,
  input: css`
    background-color: #ffffff;
  `,
  inputV2: css`
    background-color: #f7f8fa;
    @media (min-width: ${theme.breakpoints.md}) {
      background-color: #ffffff;
    }
  `,
  inputV3: css`
    background-color: #ffffff;
  `,
}));

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    keepErrorSpace,
    value,
    defaultValue,
    suffix,
    label,
    error,
    onFocus,
    onBlur,
    showTooltip,
    tooltip,
    tooltipPlacement = 'bottom-end',
    tooltipIcon,
    ...otherProps
  } = props;
  const classes = useStyles();
  const [focused, setFocused] = useState(otherProps.autoFocus);
  const [active, setActive] = useState(() => isNonEmpty(value) || isNonEmpty(defaultValue));

  useEffect(() => {
    if (!focused) {
      setActive(isNonEmpty(value));
    }
  }, [value, focused]);

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setFocused(true);
    setActive(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setFocused(false);
    if (!isNonEmpty(e.target.value)) {
      setActive(false);
    }
    onBlur?.(e);
  };

  const handleSuffixClick = (e: MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  return (
    <InputWrapper
      error={error}
      keepErrorSpace={keepErrorSpace}
      showTooltip={showTooltip}
      tooltip={tooltip}
      tooltipPlacement={tooltipPlacement}
      tooltipIcon={tooltipIcon}
      name={otherProps.name}
    >
      <InputContainer
        css={
          [
            classes.wrapper,
            active && classes.active,
            error && classes.error,
            props.disabled && classes.disabled,
          ] as any
        }
      >
        {label && (
          <InputLabel css={[active && classes.activeLabel, classes.labelGreyBackgroundV3] as any}>
            {label}
          </InputLabel>
        )}
        <StyledInput
          {...otherProps}
          value={value === null ? '' : value}
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
          css={classes.inputV3 as any}
        />
        {suffix && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <span css={classes.suffix as any} onClick={handleSuffixClick}>
            {suffix}
          </span>
        )}
      </InputContainer>
    </InputWrapper>
  );
});
